// Consolidación — revisión humana de la memoria gobernada (U-CogNet).
// Pinta EXACTAMENTE lo que la VM devuelve en /consolidation/cycle: dos colas
// (conflictos + cambios propuestos), cada una self-contained (texto legal de
// ambas normas con tier, recomendación del modelo, checks jurídicos, firma).
// El proxy de Orión inyecta org_id + reviewer server-side → audit trail confiable.
// Guía de integración del ingeniero de la VM (2026-08-02) implementada aquí.

import { useCallback, useEffect, useState } from 'react';
import {
  Brain, RefreshCw, AlertCircle, GitMerge, Scale, Check, X, HelpCircle, Clock,
  ShieldCheck, Loader2, Bot, PenLine,
} from 'lucide-react';

import {
  consolidationApi,
  unitStatement,
  unitSrc,
  unitTier,
  type CycleResponse,
  type ChangeProposal,
  type ConflictCase,
  type MemoryUnit,
  type Recommendation,
  type SharedScope,
  type Verdict,
} from '../api';

function pct(n?: number): string {
  return typeof n === 'number' ? `${Math.round(n * 100)}%` : '—';
}

const VERDICT_ES: Record<string, string> = {
  approve: 'aprobar', reject: 'rechazar', needs_review: 'necesita estudio', defer: 'aplazar',
};

function riskClass(risk?: string): string {
  if (risk === 'high') return 'border-destructive/40 bg-destructive/10 text-destructive';
  if (risk === 'medium') return 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400';
  return 'border-border bg-muted/50 text-muted-foreground';
}

// Chip de nivel de norma (federal / estatal / municipal).
function TierChip({ tier }: { tier?: string }) {
  if (!tier) return null;
  const lbl = ({ federal: 'FEDERAL', estatal: 'ESTATAL', municipal: 'MUNICIPAL' } as Record<string, string>)[tier] || tier.toUpperCase();
  const cls = tier === 'federal'
    ? 'border-violet-500/40 bg-violet-500/10 text-violet-600 dark:text-violet-300'
    : tier === 'estatal'
      ? 'border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300'
      : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300';
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide border ${cls}`}>{lbl}</span>;
}

// Caja con una norma: tipo + tier + enunciado + ley fuente.
function UnitCard({ label, u }: { label: string; u?: MemoryUnit }) {
  const text = unitStatement(u);
  const src = unitSrc(u);
  const tier = unitTier(u);
  const type = u && typeof u !== 'string' ? (u.type || '') : '';
  return (
    <div className="rounded-lg border border-border bg-background p-3 text-sm text-foreground">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{type || `Unidad ${label}`}</span>
        <TierChip tier={tier} />
      </div>
      {text || <span className="text-muted-foreground">—</span>}
      {src && <div className="text-[11px] text-muted-foreground mt-1.5 italic">📖 {src}</div>}
    </div>
  );
}

// Recomendación del modelo (orienta; NO decide).
function RecBox({ rec }: { rec?: Recommendation }) {
  if (!rec || !rec.verdict) return null;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-sm">
      <Bot size={15} className="text-violet-500 mt-0.5 shrink-0" />
      <div className="text-foreground">
        <b>Recomienda: {VERDICT_ES[rec.verdict] || rec.verdict}</b>
        {rec.rationale ? <span className="text-muted-foreground"> — {rec.rationale}</span> : null}
      </div>
    </div>
  );
}

// Check jurídico (✓ / ✗ / ? indeterminado).
function ScopeCheck({ v, label }: { v?: boolean | null; label: string }) {
  const icon = v === true ? '✓' : v === false ? '✗' : '?';
  const cls = v === true
    ? 'text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
    : v === false
      ? 'text-destructive border-destructive/30 bg-destructive/10'
      : 'text-muted-foreground border-border bg-muted/40';
  return <span className={`inline-flex items-center gap-1 text-[11px] rounded-md border px-2 py-0.5 ${cls}`}>{icon} {label}</span>;
}

function VerdictBadge({ verdict }: { verdict?: Verdict | null }) {
  if (!verdict) return null;
  const cls = verdict === 'approve' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
    : verdict === 'reject' ? 'bg-destructive/15 text-destructive'
      : 'bg-muted text-muted-foreground';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>veredicto: {VERDICT_ES[verdict] || verdict}</span>;
}

// Botones de veredicto + nota + ayuda (la semántica difiere propuesta vs conflicto).
function VerdictActions({
  kind, busy, note, onNote, onDecide,
}: {
  kind: 'proposal' | 'conflict';
  busy: boolean;
  note: string;
  onNote: (v: string) => void;
  onDecide: (v: Verdict) => void;
}) {
  const help = kind === 'conflict'
    ? 'Aprobar = confirmo que es una contradicción REAL a corregir · Rechazar = NO es contradicción (distinto ámbito/vigencia/especialidad) · Necesita estudio = a otro experto · Aplazar = posponer.'
    : 'Aprobar = aplicar el cambio a la memoria (reversible) · Rechazar = descartar · Necesita estudio = a otro experto · Aplazar = posponer.';
  const base = 'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50';
  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 mb-2">
        <PenLine size={13} className="text-muted-foreground shrink-0" />
        <input
          value={note}
          onChange={(e) => onNote(e.target.value)}
          placeholder="Nota / fundamento (opcional, queda en el acta)"
          className="flex-1 min-w-0 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={busy} onClick={() => onDecide('approve')}
          className={`${base} border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Aprobar
        </button>
        <button type="button" disabled={busy} onClick={() => onDecide('reject')}
          className={`${base} border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20`}>
          <X size={13} /> Rechazar
        </button>
        <button type="button" disabled={busy} onClick={() => onDecide('needs_review')}
          className={`${base} border-border text-foreground hover:bg-muted`}>
          <HelpCircle size={13} /> Necesita estudio
        </button>
        <button type="button" disabled={busy} onClick={() => onDecide('defer')}
          className={`${base} border-border text-muted-foreground hover:bg-muted ml-auto`}>
          <Clock size={13} /> Aplazar
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{help}</p>
    </div>
  );
}

export function ConsolidationPage() {
  const [cycle, setCycle] = useState<CycleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [decided, setDecided] = useState<Record<string, Verdict>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await consolidationApi.getCycle();
      setCycle(data);
      setDecided({});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la consolidación');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = useCallback(
    async (id: string, body: { proposal_id?: string; case_id?: string }, verdict: Verdict) => {
      setBusyId(id);
      try {
        // Se manda proposal_id Y case_id (el backend usa el que aplique) + la nota.
        await consolidationApi.decide({ ...body, verdict, notes: notes[id] || undefined });
        setDecided((prev) => ({ ...prev, [id]: verdict })); // optimista: sale de la cola
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo registrar el veredicto');
      } finally {
        setBusyId(null);
      }
    },
    [notes],
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 text-muted-foreground">
        <Loader2 size={28} className="animate-spin mb-3" />
        Cargando ciclo de consolidación…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-10 flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-4 py-3">
        <AlertCircle size={16} /> {error}
        <button type="button" onClick={load} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted">
          <RefreshCw size={13} /> Reintentar
        </button>
      </div>
    );
  }

  const s = cycle?.cycle_summary;
  const rp = cycle?.review_progress;
  const invariants = cycle?.invariants || {};
  const conflicts = (cycle?.conflict_cases || []).filter((c) => !c.expert_verdict && !decided[c.case_id]);
  const proposals = (cycle?.change_proposals || []).filter((p) => !p.expert_verdict && !decided[p.proposal_id]);

  const INV_ES: Record<string, string> = {
    nothing_canonical_auto_applied: 'nada canónico auto-aplicado',
    every_proposal_reversible: 'todo reversible',
    every_proposal_has_rollback: 'con rollback',
    no_external_info: 'sin info externa',
  };

  return (
    <div className="space-y-4">
      {/* Header + progreso */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Brain size={20} className="text-foreground" />
          <div>
            <h1 className="text-base font-semibold text-foreground">Consolidación de la memoria</h1>
            <p className="text-xs text-muted-foreground">
              Revisión humana de fusiones y conflictos del cerebro soberano
              {s?.cycle_id ? ` · ciclo ${s.cycle_id}` : ''}
            </p>
          </div>
          <button type="button" onClick={load}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted">
            <RefreshCw size={13} /> Actualizar
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          {[
            { k: 'Unidades', v: s?.units_evaluated },
            { k: 'Pendientes de revisión', v: s?.changes_pending_review },
            { k: 'Conflictos', v: s?.conflicts_detected },
            { k: 'Fusiones propuestas', v: s?.merges_proposed },
          ].map((kpi) => (
            <div key={kpi.k} className="rounded-lg border border-border bg-background px-3 py-2">
              <div className="text-lg font-semibold text-foreground">{kpi.v ?? '—'}</div>
              <div className="text-[11px] text-muted-foreground">{kpi.k}</div>
            </div>
          ))}
        </div>

        {/* Progreso de revisión */}
        {rp && (
          <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
            <span>Revisados {rp.reviewed}/{rp.total}</span>
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden max-w-[240px]">
              <div className="h-full bg-emerald-500" style={{ width: `${Math.round((rp.pct || 0) * 100)}%` }} />
            </div>
            <span>{pct(rp.pct)}</span>
          </div>
        )}

        {/* Invariantes de gobernanza (garantías) */}
        {Object.keys(invariants).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {Object.entries(invariants).map(([k, ok]) => (
              <span key={k}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${ok ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-destructive/10 text-destructive'}`}>
                <ShieldCheck size={11} /> {INV_ES[k] || k.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Conflictos */}
      <section className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Scale size={16} className="text-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Conflictos por resolver</h2>
          <span className="text-xs text-muted-foreground">({conflicts.length})</span>
        </div>
        {conflicts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Sin conflictos pendientes 🎉</p>
        ) : (
          <div className="space-y-3 mt-2">
            {conflicts.map((c: ConflictCase) => {
              const sc: SharedScope = c.shared_scope || {};
              return (
                <div key={c.case_id} className="rounded-lg border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted/50 text-muted-foreground">
                      {c.classification || c.conflict_type || 'conflicto'}
                    </span>
                    {c.requires_lawyer_review && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        <Scale size={11} /> Requiere abogado
                      </span>
                    )}
                    {c.prevails && c.prevails !== 'none' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300">
                        prevalecería: norma {String(c.prevails).toUpperCase()}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground">conf. {pct(c.confidence)}</span>
                    <VerdictBadge verdict={c.expert_verdict} />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 mt-2">
                    <UnitCard label="A" u={c.a} />
                    <UnitCard label="B" u={c.b} />
                  </div>
                  {/* Checks jurídicos */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <ScopeCheck v={sc.same_subject} label="mismo sujeto" />
                    <ScopeCheck v={sc.same_fact_pattern} label="mismo supuesto" />
                    <ScopeCheck v={sc.same_time_scope} label="vigencia" />
                    <ScopeCheck v={sc.same_territory} label="territorio" />
                  </div>
                  {c.reasoning_summary && (
                    <p className="text-sm text-foreground mt-2">{c.reasoning_summary}</p>
                  )}
                  <RecBox rec={c.recommendation} />
                  <VerdictActions
                    kind="conflict"
                    busy={busyId === c.case_id}
                    note={notes[c.case_id] || ''}
                    onNote={(v) => setNotes((p) => ({ ...p, [c.case_id]: v }))}
                    onDecide={(v) => decide(c.case_id, { case_id: c.case_id, proposal_id: c.case_id }, v)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Propuestas de cambio */}
      <section className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <GitMerge size={16} className="text-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Cambios propuestos</h2>
          <span className="text-xs text-muted-foreground">({proposals.length})</span>
        </div>
        {proposals.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Sin cambios pendientes 🎉</p>
        ) : (
          <div className="space-y-3 mt-2">
            {proposals.map((p: ChangeProposal) => (
              <div key={p.proposal_id} className="rounded-lg border border-border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted/50 text-muted-foreground">
                    {p.operation}
                  </span>
                  {p.risk_level && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${riskClass(p.risk_level)}`}>
                      riesgo {p.risk_level}
                    </span>
                  )}
                  {p.requires_human_review && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">→ requiere revisión</span>
                  )}
                  {p.reversible && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                      <RefreshCw size={11} /> reversible
                    </span>
                  )}
                  {p.governance?.decision_id && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title={p.governance.decision_id}>
                      ✓ {p.governance.decision_id.slice(0, 12)}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">conf. {pct(p.confidence)}</span>
                  <VerdictBadge verdict={p.expert_verdict} />
                </div>
                {p.justification && (
                  <p className="text-sm text-foreground mt-2">{p.justification}</p>
                )}
                {(p.a || p.b) && (
                  <div className="grid gap-2 sm:grid-cols-2 mt-2">
                    <UnitCard label="A" u={p.a} />
                    <UnitCard label="B" u={p.b} />
                  </div>
                )}
                <RecBox rec={p.recommendation} />
                <VerdictActions
                  kind="proposal"
                  busy={busyId === p.proposal_id}
                  note={notes[p.proposal_id] || ''}
                  onNote={(v) => setNotes((prev) => ({ ...prev, [p.proposal_id]: v }))}
                  onDecide={(v) => decide(p.proposal_id, { proposal_id: p.proposal_id, case_id: p.proposal_id }, v)}
                />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default ConsolidationPage;
